import React, { useMemo, useState } from 'react';
import { Pressable, ScrollView, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { DateTime } from 'luxon';
import Avatar from '@/components/People/Avatar';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import Unicon from '@/components/General/Unicon';
import SegmentedTabs from '@/components/General/SegmentedTabs';
import Tooltip from '@/components/General/Tooltip';
import DropdownMenu from '@/components/General/DropdownMenu';
import {
    COPY,
    ackKey,
    blockCopy,
    dataQualityCopy,
    groupTargetsByEvent,
    buildPreviewGrid,
    personLabel,
    responseCopy,
    taskUnplannableReason,
    unfilledCopy,
    warningCopy,
} from './autoScheduleModel';

const Badge = ({ label, colorVariant = 'tasks', testID }) => (
    <View testID={testID} style={styles.badge(colorVariant)}>
        <Text style={styles.badgeText(colorVariant)}>{label}</Text>
    </View>
);

const PersonRow = ({ person, badge, badgeVariant, note, locked }) => (
    <View style={styles.personRow}>
        <Avatar profile={person} size={28} disableBorder />
        <View style={styles.personCopy}>
            <Text style={styles.personName} numberOfLines={1}>{personLabel(person)}</Text>
            {!!note && <Text style={styles.personNote} numberOfLines={2}>{note}</Text>}
        </View>
        {locked && <Unicon name="lock-closed-outline" size={13} colorVariant="textSecondary" />}
        {!!badge && <Badge label={badge} colorVariant={badgeVariant} />}
    </View>
);

const formatEventHeading = (event, timezone) => {
    if (!event?.startTime) return event?.eventName || 'Event';
    const start = DateTime.fromISO(event.startTime).setZone(timezone);
    const end = event.endTime ? DateTime.fromISO(event.endTime).setZone(timezone) : null;
    const date = start.isValid ? start.toFormat('EEE, LLL d') : '';
    const time = start.isValid ? start.toFormat('h:mm a') : '';
    const endText = end?.isValid ? `–${end.toFormat('h:mm a')}` : '';
    return `${event.eventName}${date ? ` · ${date}` : ''}${time ? ` ${time}${endText}` : ''}`;
};

const TaskCard = ({ target, selectedIDs, peopleByID, isEdited, acks, conflicts, pendingAcks, onEdit, onToggleAck, exclusionFor, onToggleExclusion }) => {
    const selected = selectedIDs.map(id => peopleByID.get(id)).filter(Boolean);
    const metaByID = useMemo(() => {
        const map = new Map();
        target.candidates.forEach(c => map.set(c.person.id, c));
        return map;
    }, [target]);

    // Bounded candidate list — external queryResult only; SheetSelector's
    // internal people query stays disabled (queryResult present) and
    // peopleSubset is never used, so "Show everyone" cannot appear.
    const candidateQueryResult = useMemo(() => ({
        data: { pages: [{ data: target.candidates.map(c => c.person) }] },
        isFetching: false,
    }), [target]);

    const unplannable = taskUnplannableReason(target);
    const remaining = Math.max(0, (target.openCount ?? selected.length) - selected.length);
    const overCapacity = Number.isFinite(target.openCount) && selected.length > target.openCount;
    const unfilled = remaining > 0 ? unfilledCopy(target.unfilledReasonCode) : null;
    const qualityWarnings = (target.dataQualityWarnings || [])
        .map(w => (typeof w === 'string' ? dataQualityCopy(w) || w : w?.message || dataQualityCopy(w?.code)))
        .filter(Boolean);

    return (
        <View testID={`AutoScheduleTask-${target.taskInstanceID}`} style={styles.taskCard}>
            <View style={styles.taskHeader}>
                <View style={styles.taskTitleWrap}>
                    <Text style={styles.taskTitle}>{target.title}</Text>
                    <Text style={styles.taskMeta}>
                        {Number.isFinite(target.needed) ? `${target.needed} needed` : 'Volunteers needed'}
                        {Number.isFinite(target.openCount) ? ` · ${target.openCount} open` : ''}
                        {remaining > 0 ? ` · ${remaining} still open` : ''}
                    </Text>
                </View>
                {isEdited && <Badge label={COPY.badgeEdited} colorVariant="info" />}
            </View>

            {target.existing.map(e => (
                <PersonRow
                    key={`existing-${e.person.id}`}
                    person={e.person}
                    badge={COPY.badgeExisting}
                    badgeVariant="textSecondary"
                    note={responseCopy(e.response)}
                    locked
                />
            ))}

            <View testID={`AutoScheduleAssignees-${target.taskInstanceID}`} style={styles.suggestedBlock}>
                {selected.length === 0 ? (
                    <View style={styles.suggestedEmpty}>
                        <Unicon name="person-add-outline" size={15} colorVariant="tasks" />
                        <Text style={styles.suggestedEmptyText}>{unplannable ? 'No suggestions' : 'No suggestions — choose volunteers or leave open'}</Text>
                    </View>
                ) : selected.map(person => {
                    const proposal = target.proposed.find(p => p.person.id === person.id);
                    const conflict = conflicts.get(`${target.taskInstanceID}|${person.id}`);
                    const exclusionID = exclusionFor?.(target, person.id);
                    return (
                        <View key={person.id} style={styles.personRow}>
                            <Avatar profile={person} size={28} disableBorder />
                            <View style={styles.personCopy}>
                                <Text style={styles.personName}>{personLabel(person)}</Text>
                                {!!conflict && (
                                    <Text style={styles.conflictText}>
                                        Conflicts with “{conflict.otherTitle}” on {conflict.otherEventName}
                                    </Text>
                                )}
                            </View>
                            {!!proposal?.reasonText && (
                                <Tooltip
                                    testID={`AutoScheduleReason-${target.taskInstanceID}-${person.id}`}
                                    title={`${personLabel(person)}: ${proposal.reasonText}`}
                                    accessibilityLabel={`Why ${personLabel(person)}? ${proposal.reasonText}`}
                                    appearOnTouch
                                    style={styles.reasonButton}
                                >
                                    <Unicon name="help-circle-outline" size={18} colorVariant="textSecondary" />
                                </Tooltip>
                            )}
                            {!!onToggleExclusion && !!target.taskID && !!target.eventID && (
                                <DropdownMenu
                                    testID={`AutoSchedulePersonMenu-${target.taskInstanceID}-${person.id}`}
                                    title={personLabel(person)}
                                    options={[{
                                        id: exclusionID ? 'allow-suggestions' : 'never-suggest',
                                        name: exclusionID ? 'Allow suggestions again' : 'Never suggest for this task on this event',
                                        subtitle: exclusionID
                                            ? `${personLabel(person)} can be suggested for ${target.title} on ${target.eventName} again.`
                                            : `${personLabel(person)} won't be suggested for ${target.title} on ${target.eventName} in future runs.`,
                                        icon: {
                                            ios: exclusionID ? 'checkmark.circle' : 'hand.raised',
                                            android: exclusionID ? 'check_circle' : 'block',
                                            web: exclusionID ? 'checkmark-circle-outline' : 'hand-right-outline',
                                        },
                                        onSelect: () => onToggleExclusion(target, person, exclusionID),
                                    }]}
                                    triggerComponent={
                                        <View testID={`AutoSchedulePersonMenuIcon-${target.taskInstanceID}-${person.id}`} style={styles.menuTrigger}>
                                            <Unicon name="ellipsis-horizontal" size={16} colorVariant="textSecondary" />
                                        </View>
                                    }
                                />
                            )}
                            <Badge label={proposal ? COPY.badgeSuggested : COPY.badgeEdited} colorVariant={proposal ? 'tasks' : 'info'} />
                        </View>
                    );
                })}
            </View>

            <View style={styles.taskActions}>
                <SheetSelector
                    type="people"
                    multiple
                    forceModal
                    title={`${target.title} - Suggestions`}
                    value={selected}
                    queryResult={candidateQueryResult}
                    excludeIds={target.existing.map(e => e.person.id)}
                    errorFn={(person) => {
                        const meta = metaByID.get(person.id);
                        if (!meta?.hardBlocked) return [];
                        const codes = meta.blockCodes.length ? meta.blockCodes : ['unavailable'];
                        return codes.map(blockCopy);
                    }}
                    disableErroredOptions
                    onChange={(items) => onEdit(target.taskInstanceID, items)}
                >
                    <View testID={`AutoScheduleEdit-${target.taskInstanceID}`} style={styles.editHint}>
                        <Unicon name="create-outline" size={13} colorVariant="tasks" />
                        <Text style={styles.editHintText}>Edit</Text>
                    </View>
                </SheetSelector>

                {selected.length > 0 && (
                    <Pressable
                        testID={`AutoScheduleLeaveEmpty-${target.taskInstanceID}`}
                        accessibilityRole="button"
                        accessibilityLabel={`Leave ${target.title} empty`}
                        onPress={() => onEdit(target.taskInstanceID, [])}
                        style={styles.leaveEmpty}
                    >
                        <Unicon name="close-outline" size={13} colorVariant="textSecondary" />
                        <Text style={styles.leaveEmptyText}>Leave empty</Text>
                    </Pressable>
                )}
            </View>

            {overCapacity && (
                <Text accessibilityLiveRegion="polite" style={styles.errorText}>
                    Too many people selected — {target.openCount} open position{target.openCount === 1 ? '' : 's'}.
                </Text>
            )}
            {!!unfilled && <Text style={styles.unfilledText}>{unfilled}</Text>}
            {!!unplannable && <Text style={styles.unfilledText}>{unplannable}</Text>}
            {qualityWarnings.map((w, i) => <Text key={i} style={styles.qualityText}>{w}</Text>)}

            {(pendingAcks || []).map(({ personID, code }) => {
                const person = peopleByID.get(personID);
                const acknowledged = acks.has(ackKey(target.taskInstanceID, personID, code));
                return (
                    <Pressable
                        key={ackKey(target.taskInstanceID, personID, code)}
                        testID={`AutoScheduleWarningAck-${target.taskInstanceID}-${personID}-${code}`}
                        accessibilityRole="button"
                        accessibilityLabel={`${acknowledged ? 'Acknowledged' : 'Acknowledge'} warning for ${personLabel(person)}: ${warningCopy(code)}`}
                        accessibilityState={{ selected: acknowledged }}
                        onPress={() => onToggleAck(target.taskInstanceID, personID, code)}
                        style={styles.ackChip(acknowledged)}
                    >
                        <Unicon name={acknowledged ? 'checkmark-circle' : 'alert-circle-outline'} size={14} colorVariant={acknowledged ? 'success' : 'warning'} />
                        <Text style={styles.ackText} numberOfLines={2}>
                            {personLabel(person)}: {warningCopy(code)}
                        </Text>
                        <Text style={styles.ackAction}>{acknowledged ? 'Acknowledged' : 'Acknowledge'}</Text>
                    </Pressable>
                );
            })}
        </View>
    );
};

/**
 * Read-only rendering of the generated preview with editable suggestions.
 * Nothing here writes assignments — edits only update the local draft.
 */
export default function AutoSchedulePreview({ targets, draft, peopleByID, acks, conflicts, pendingAcksByTask, isEditedTask, onEdit, onToggleAck, exclusionFor, onToggleExclusion, timezone }) {
    const [view, setView] = useState('grid');
    const events = useMemo(() => groupTargetsByEvent(targets), [targets]);
    const grid = useMemo(() => buildPreviewGrid(targets), [targets]);
    const renderTask = target => (
        <TaskCard
            key={target.taskInstanceID}
            target={target}
            selectedIDs={draft[target.taskInstanceID] || []}
            peopleByID={peopleByID}
            isEdited={isEditedTask(target)}
            acks={acks}
            conflicts={conflicts}
            pendingAcks={pendingAcksByTask.get(target.taskInstanceID) || []}
            onEdit={onEdit}
            onToggleAck={onToggleAck}
            exclusionFor={exclusionFor}
            onToggleExclusion={onToggleExclusion}
        />
    );
    return (
        <View testID="AutoSchedulePreview" style={styles.list}>
            <View style={styles.viewToolbar}>
                <Text style={styles.viewHint}>{view === 'grid' ? 'Compare assignments across dates. Scroll sideways for more.' : 'Review one event at a time.'}</Text>
                <View style={styles.viewToggle}>
                    <SegmentedTabs
                        tabs={[{ key: 'grid', label: 'Grid' }, { key: 'list', label: 'List' }]}
                        value={view}
                        onChange={setView}
                        testIDPrefix="AutoScheduleView"
                    />
                </View>
            </View>
            {view === 'grid' ? (
                <ScrollView
                    testID="AutoScheduleGrid"
                    horizontal
                    nestedScrollEnabled
                    showsHorizontalScrollIndicator
                    keyboardShouldPersistTaps="handled"
                    style={styles.gridScroll}
                    contentContainerStyle={styles.gridContent}
                >
                    <View style={styles.grid}>
                        <View style={styles.gridRow}>
                            <View style={styles.gridLabel}>
                                <Text style={styles.eventTitle}>Tasks / events</Text>
                            </View>
                            {grid.events.map(event => (
                                <View key={event.eventInstanceID} style={styles.gridColumnHeader}>
                                    <Text style={styles.eventTitle}>{formatEventHeading(event, timezone)}</Text>
                                    {!!event.locationLabel && <Text style={styles.eventMeta}>{event.locationLabel}</Text>}
                                </View>
                            ))}
                        </View>
                        {grid.rows.map(row => (
                            <View key={row.key} testID={`AutoScheduleGridRow-${row.key}`} style={styles.gridRow}>
                                <View style={styles.gridLabel}>
                                    <Text style={styles.teamName}>{row.teamName}</Text>
                                    <Text style={styles.taskTitle}>{row.title}</Text>
                                </View>
                                {grid.events.map(event => (
                                    <View key={event.eventInstanceID} style={styles.gridCell}>
                                        {row.byEvent.get(event.eventInstanceID)?.map(renderTask) || (
                                            <Text style={styles.eventMeta}>Not scheduled</Text>
                                        )}
                                    </View>
                                ))}
                            </View>
                        ))}
                    </View>
                </ScrollView>
            ) : <View testID="AutoScheduleList" style={styles.list}>{events.map(event => (
                <View key={event.eventInstanceID} style={styles.eventGroup}>
                    <View style={styles.eventHeader}>
                        <Unicon name="calendar-outline" size={15} colorVariant="tasks" />
                        <View style={styles.eventHeaderText}>
                            <Text style={styles.eventTitle}>{formatEventHeading(event, timezone)}</Text>
                            {!!event.locationLabel && <Text style={styles.eventMeta}>{event.locationLabel}</Text>}
                        </View>
                    </View>
                    {event.teams.map(team => (
                        <View key={team.teamID || team.teamName} style={styles.teamGroup}>
                            <View style={styles.teamHeader}>
                                <Unicon name="people-outline" size={14} colorVariant="textSecondary" />
                                <Text style={styles.teamName}>{team.teamName}</Text>
                            </View>
                            {team.targets.map(renderTask)}
                        </View>
                    ))}
                </View>
            ))}</View>}
        </View>
    );
}

const styles = StyleSheet.create(({ colors, fonts, shadows }) => ({
    list: { gap: 18 },
    viewToolbar: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 },
    viewHint: { color: colors.textSecondary, fontFamily: fonts.body, fontSize: 12, flexShrink: 1 },
    viewToggle: { width: 160 },
    gridScroll: { flexGrow: 0 },
    gridContent: { paddingBottom: 12 },
    grid: { gap: 2 },
    gridRow: { flexDirection: 'row', alignItems: 'stretch' },
    gridLabel: { width: 156, padding: 12, gap: 6, backgroundColor: colors.surfaceVariant, borderRadius: 8 },
    gridColumnHeader: { width: 288, padding: 12, gap: 4 },
    gridCell: { width: 288, padding: 4, gap: 6 },
    eventGroup: { gap: 8 },
    eventHeader: { flexDirection: 'row', alignItems: 'flex-start', gap: 8 },
    eventHeaderText: { flex: 1 },
    eventTitle: { color: colors.text, fontFamily: fonts.heading, fontSize: 15 },
    eventMeta: { color: colors.textSecondary, fontSize: 12, marginTop: 1 },
    teamGroup: { gap: 6 },
    teamHeader: { flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 4 },
    teamName: { color: colors.textSecondary, fontFamily: fonts.bold, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6 },
    taskCard: { backgroundColor: colors.surface, borderRadius: 12, padding: 12, gap: 6, boxShadow: shadows?.card },
    taskHeader: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 },
    taskTitleWrap: { flex: 1 },
    taskTitle: { color: colors.text, fontFamily: fonts.heading, fontSize: 14 },
    taskMeta: { color: colors.textSecondary, fontSize: 11, marginTop: 2 },
    personRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 3 },
    personCopy: { flex: 1, minWidth: 0 },
    personName: { color: colors.text, fontSize: 13, fontFamily: fonts.heading },
    personNote: { color: colors.textSecondary, fontSize: 11, marginTop: 1 },
    badge: colorVariant => ({ backgroundColor: colors[`${colorVariant}Container`] || colors.surfaceVariant, borderRadius: 6, paddingHorizontal: 6, paddingVertical: 2 }),
    badgeText: colorVariant => ({ color: colors[colorVariant] || colors.textSecondary, fontFamily: fonts.bold, fontSize: 10 }),
    suggestedBlock: { borderRadius: 10, backgroundColor: colors.surfaceVariant, padding: 8, gap: 4 },
    suggestedEmpty: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 6 },
    suggestedEmptyText: { color: colors.textSecondary, fontSize: 12, flex: 1 },
    editHint: { flexDirection: 'row', alignItems: 'center', gap: 4, alignSelf: 'flex-end' },
    editHintText: { color: colors.tasks, fontFamily: fonts.bold, fontSize: 11 },
    taskActions: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
    reasonButton: { padding: 8, minWidth: 36, minHeight: 36, alignItems: 'center', justifyContent: 'center' },
    menuTrigger: { padding: 6, minWidth: 32, minHeight: 32, alignItems: 'center', justifyContent: 'center' },
    conflictText: { color: colors.warning, fontSize: 11, marginTop: 1 },
    errorText: { color: colors.error, fontSize: 12 },
    unfilledText: { color: colors.warning, fontSize: 12 },
    qualityText: { color: colors.textSecondary, fontSize: 11 },
    ackChip: acknowledged => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        borderRadius: 9,
        paddingHorizontal: 8,
        paddingVertical: 6,
        backgroundColor: acknowledged ? colors.successContainer : colors.warningContainer,
    }),
    ackText: { flex: 1, color: colors.text, fontSize: 11 },
    ackAction: { color: colors.tasks, fontFamily: fonts.bold, fontSize: 11 },
    leaveEmpty: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingVertical: 4, paddingHorizontal: 6 },
    leaveEmptyText: { color: colors.textSecondary, fontFamily: fonts.bold, fontSize: 11 },
}));
